import type { ToastData, ToastTone } from "../components/Toast";
import { useStore } from "../store";
import type { AnyView } from "../variants/types";
import { wordsOf } from "../variants/words";
import { balootCallText } from "./balootText";
import type { T } from "./tableText";
import type { Seats } from "./toastParts";

/** Between hands, in each game's own words. */
function handEndToast(
  view: AnyView,
  seats: Seats,
  yourSeat: number | null,
  t: T,
): ToastData | null {
  return view.phase === "hand-end" ? wordsOf(view).handEnd(view, seats, yourSeat, t) : null;
}

export const EVENT_MS = 3_000;

/** What an event means for the colour of its toast; anything else is neutral. */
const EVENT_TONE: Readonly<Record<string, ToastTone>> = {
  "nil-made": "success",
  "nil-failed": "error",
  kaboot: "success",
  doubled: "warning",
  "contract-forced": "warning",
};

type Event = { code: string; data?: Record<string, string | number> | undefined };

/** Spades events worth a sentence. */
function spadesEventText(code: string, name: string, t: T): string | null {
  if (code === "bids-done") return t("spades.event.bids-done");
  if (code === "nil-made" || code === "nil-failed") return t(`spades.event.${code}`, { name });
  return null;
}

function trumpText(last: Event, t: T): string | null {
  const suit = last.data?.suit;
  return typeof suit === "string"
    ? t("table.event.trump-chosen", { suit: t(`suit.${suit}` as "suit.spades") })
    : null;
}

/** Batak and Tarneeb: a redeal, the bid taken (forced or not), the trump. */
function biddingEventText(last: Event, name: string, t: T): string | null {
  const amount = last.data?.amount;
  switch (last.code) {
    case "redeal":
      return t("table.event.redeal");
    case "contract-forced":
      return t("table.event.contract-forced", { name, amount: Number(amount ?? 0) });
    case "contract-won":
      // Baloot's contract carries a mode, not an amount; its own sentence follows.
      return typeof amount === "number" ? t("table.event.contract-won", { name, amount }) : null;
    case "trump-chosen":
      return trumpText(last, t);
    default:
      return null;
  }
}

/** Baloot events: the contract, a clean sweep, king and queen of trumps, doubles, projects. */
function balootEventText(last: Event, name: string, t: T): string | null {
  if (last.code === "contract-won" && typeof last.data?.mode === "string") {
    const suit =
      typeof last.data.suit === "string" && last.data.suit !== "" ? last.data.suit : null;
    const call = balootCallText({ mode: last.data.mode as "sun" | "hokum", trump: suit }, t);
    return t("baloot.event.contract-won", { name, call });
  }
  if (last.code === "kaboot") return t("baloot.event.kaboot");
  if (last.code === "baloot") return t("baloot.event.baloot", { name });
  if (last.code === "doubled")
    return t("baloot.event.doubled", {
      level: t(`baloot.double.${last.data?.level}` as "baloot.double.2"),
    });
  if (last.code === "project")
    return t("baloot.event.project", {
      name,
      project: t(`baloot.project.${last.data?.kind}` as "baloot.project.sira"),
    });
  return null;
}

/** The player an event names: the name for its sentence, the avatar when it has a seat. */
function subjectOf(last: Event & { seat?: number | undefined }, seats: Seats) {
  const seat = last.seat ?? Number(last.data?.seat ?? 0);
  const member = last.seat === undefined ? null : (seats[last.seat] ?? null);
  return { name: seats[seat]?.name ?? "?", member };
}

function eventTitle(last: Event, name: string, t: T): string | null {
  return (
    biddingEventText(last, name, t) ??
    spadesEventText(last.code, name, t) ??
    balootEventText(last, name, t)
  );
}

/** A fresh game event worth a sentence, for three seconds, with the player it names. */
function eventToast(view: AnyView, seats: Seats, t: T): ToastData | null {
  const { events, eventsAt } = useStore.getState();
  const last = events[events.length - 1];
  if (!last || Date.now() - eventsAt > EVENT_MS) return null;
  const { name, member } = subjectOf(last, seats);
  // The game's own sentence first; the sentences the games share follow.
  const title = wordsOf(view).eventText?.(last, view, seats, t) ?? eventTitle(last, name, t);
  return title === null ? null : { title, tone: EVENT_TONE[last.code] ?? "neutral", member };
}

/** The toast over the table: an error, the hand result, your contract, or a fresh event. */
export function toastFor(
  view: AnyView,
  seats: Seats,
  yourSeat: number | null,
  lastError: string | null,
  t: T,
  err: (code: string) => string,
): ToastData | null {
  if (lastError) return { title: err(lastError), tone: "error" };
  return (
    handEndToast(view, seats, yourSeat, t) ??
    wordsOf(view).turnToast?.(view, yourSeat, t) ??
    eventToast(view, seats, t)
  );
}
